import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import {
  Typography,
  Paper
} from "@mui/material";
import ArrayEditor from './ArrayEditor.js'
import Page from "../../layout/Page.js";
import FormViewCommandBar from "../../commandBar/FormViewCommandBar.js";
import { metadataLabelInUse } from "../data/settings.js";
import { getResourceLibraryFileByPath, uploadFileToPath } from '../data/index.js'
import EventBus from "../../utils/EventBus.js";
import EventNames from "../../utils/EventNames.js";
import CacheContext from '../../cache/CacheContext.js'
import ReassignmentDialog from "./ReassignmentDialog.js";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog.js";
import { getSiteConfig } from "../../community/siteProvider.js";
import { useLocation } from "react-router-dom";
import TabPanel from "../../form/TabPanel.js";

export default function CommunityHubAdminPage(props = {}) {
  const [config, setConfig] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null)
  const [driveConfig, setDriveConfig] = useState(null)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reassignmentDialogOpen, setReassignmentDialogOpen] = useState('');
  const [reassignmentDialogProps, setReassignmentDialogProps] = useState({});
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState('');
  const [confirmDeleteDialogProps, setConfirmDeleteDialogProps] = useState({});

  const cacheContext = useContext(CacheContext)

  const { pathname } = useLocation()
    
  const siteKey = useMemo(() => {
    return pathname.split('/').filter(Boolean)[0] ?? null
  }, [pathname])

  const loadSiteConfig = () => {
    const _siteConfig = getSiteConfig()
    setSiteConfig(_siteConfig)
    setDriveConfig(_siteConfig.libraryDrives[0])
  }
  
  const loadConfig = async (_driveConfig) => {
    setLoading(true)
    try {
      const file = await getResourceLibraryFileByPath(siteConfig.settingsFilePath, _driveConfig ?? driveConfig)
      setConfig(file)
    } catch (error) {
      console.log(error)
      setConfig({})
    }
    setLoading(false);
  }

  useEffect(() => {
    console.log(siteKey)
    loadSiteConfig();
  }, [siteKey]);

  useEffect(() => {
    if (siteConfig && driveConfig) {
      loadConfig(driveConfig)
    }
  }, [siteConfig, driveConfig])

  // const loadDriveConfig = async (_driveConfig) => {
  //   await loadConfig(_driveConfig)
  //   setDriveConfig(_driveConfig)
  // }

  // --- Save function: replace with Graph API PUT request ---
  const saveConfig = async () => {
    const { settingsFilePath } = siteConfig
    setLoading(true)
    setSaving(true);
    try {
      await uploadFileToPath(settingsFilePath, JSON.stringify(config), 'application/json', driveConfig)
      setDirty(false)
      EventBus.emit(EventNames.ALERT, {
        text: 'Successfully Saved Settings',
        duration: 2000,
        style: 'success'
      })
    } finally {
      setSaving(false);
      setLoading(false)
    }
  };

  // if (loading) return <Typography>Loading...</Typography>;

  async function onClickChip(chipName, section, key) {
    setReassignmentDialogOpen(chipName)
    setReassignmentDialogProps({
      label: chipName,
      entityType: section,
      itemType: key,
      reassignmentOptions: (config?.[section]?.[key] || []).filter(f => f !== chipName)
    })
  }

  async function validateDelete(toDelete, section, key) {
    const inUse = await metadataLabelInUse({
      tag: key === 'tags' ? toDelete : undefined,
      category: key === 'categories' ? toDelete : undefined,
      list: section,
      fetchListItems: true
    }, cacheContext, driveConfig)
    return inUse
  }

  const onCancel = () => {
    setLoading(true)
    setDirty(false)
    loadConfig()
    setLoading(false)
  }

  useEffect(() => {
    (async () => {
      if(dirty) {
        await saveConfig()
      }
    })()
  }, [config, dirty])

  const getSettingSections = useCallback(() => {
    if (!siteConfig) return []
    const settingsSections = [
      {
        sectionKey: "articles",
        sectionType: "articles",
        sectionLabel: "Articles",
        sectionDrive: siteConfig.libraryDrives[0]
      }
    ]
    if (siteConfig.libraryDrives.length > 1) {
      siteConfig.libraryDrives.forEach((d) => {
        if (d.route) {
          settingsSections.push({
            sectionKey: d.route,
            sectionType: "resources",
            sectionLabel: `${d.label} Library`,
            sectionDrive: d
          })
        }
      })
    } else {
      settingsSections.push({
        sectionKey: "resources",
        sectionType: "resources",
        sectionLabel: "Resource Library",
        sectionDrive: siteConfig.libraryDrives[0]
      })
    }
    return settingsSections
  },[siteConfig])

  const tabViews = getSettingSections().map(({sectionKey, sectionType, sectionLabel, sectionDrive}) => {
            return {
              key: [sectionKey,'tab'].join('-'),
              label: sectionLabel,
              title: undefined, // hover help text
              drive: sectionDrive,
              el: (<Paper sx={{padding: '1em', margin: '1em'}} key={sectionKey}>
                    <Typography variant="h5">{sectionLabel}</Typography>

                    {/* Numeric fields */}
                    {/* {config[section].pinCount !== undefined && (
                      <TextField
                        label="Pin Count"
                        type="number"
                        value={config[section].pinCount}
                        onChange={(e) => {
                          setDirty(true)
                          setConfig({
                            ...config,
                            [section]: {
                              ...config[section],
                              pinCount: Number(e.target.value),
                            },
                          })
                        }}
                        helperText="The number pin positions available for pinned articles."
                        sx={{ mt: 2, mr: 2 }}
                      />
                    )} */}
                    {/* {config[section].pageSize !== undefined && (
                      <TextField
                        label="Page Size"
                        type="number"
                        value={config[section].pageSize}
                        onChange={(e) => {
                          setDirty(true)
                          setConfig({ ...config, [section]: { ...config[section], pageSize: Number(e.target.value) } })
                        }}
                        sx={{ mt: 2 }}
                        helperText={<p>The number number of articles that load per category in a fresh page.<br />Additional articles are loaded once you click "Load More"</p>}
                      />
                    )} */}

                    {/* Editable arrays */}
                    {[{
                      key: "tags",
                      helperText: "",
                      label: "Tag",
                    },
                    {
                      key: "categories",
                      helperText: "",
                      label: "Category",
                    }]
                    .map(({ key, helperText, label }) => {
                      const labelOverride =
                        sectionKey === 'articles' && key === 'categories' ? 'OU Corner Categories' :
                        sectionKey === 'articles' && key === 'tags' ? 'News & OU Corner Tags' :
                        null
                      return <ArrayEditor
                        key={key}
                        label={label}
                        // section={sectionType}
                        labelOverride={labelOverride}
                        keyName={key}
                        values={config?.[sectionType]?.[key]}
                        validateDelete={(item) => validateDelete(item, sectionType, key)}
                        onClickChip={(item) => onClickChip(item, sectionType, key)}
                        onUpdate={(updatedArray, itemName, showConfirm) => {
                          const applyUpdate = () => {
                            setDirty(true)
                            setConfig((prev) => ({
                              ...prev,
                              [sectionType]: {
                                ...prev[sectionType],
                                [key]: updatedArray
                              }
                            }))
                          }
                          if(showConfirm) {
                            setConfirmDeleteDialogProps({
                              onConfirm: () => { applyUpdate(); setConfirmDeleteDialogOpen(false) },
                              itemName,
                              onClose: () => setConfirmDeleteDialogOpen(false)
                            })
                            setConfirmDeleteDialogOpen(true)
                          } else {
                            applyUpdate()
                          }
                        }}
                      />
                    })}
                  </Paper>)
            }
          })

  const onTabChange = (tabIndex) => {
    const _driveConfig = tabViews[tabIndex].drive
    setDriveConfig(_driveConfig)
  }

  return (
    <Page
      {...props}
      breadcrumb={`${siteConfig?.label} Settings`}
      isLoading={loading}
      commandBar={
      <FormViewCommandBar
        onCancel={onCancel}
        showSave={false}
        showCancel={!saving && dirty}
        showClose={false}
      />
    }
    >
      <ConfirmDeleteDialog
        open={confirmDeleteDialogOpen}
        {...confirmDeleteDialogProps}
        // onClose={}
        // onConfirm
        // itemName
        driveConfig={driveConfig}
      />

      <ReassignmentDialog
        open={reassignmentDialogOpen}
        onClose={() => {
          setReassignmentDialogOpen()
          setReassignmentDialogProps({})
        }}
        {...reassignmentDialogProps}
        driveConfig={driveConfig}
      />

        
        <TabPanel panelId='settingsTabs' key='settings' 
          tabs={tabViews} 
          tabStyle={{ textTransform: 'none', minHeight: 'unset'}}
          defaultTab={0}
          onTabChange={(idx) => onTabChange(idx)}
        />
    </Page>
  );
}